// Data for the क्रियारूप lab: every kit root plus अस, the tenses each one has, the rule of each tense in words,
// and a Hindi / English meaning for present-tense example sentences (only where it can be phrased correctly).

import type {
    Num,
    Person,
    PersonTable,
    RootClass,
    Tense
} from '@/prakrit/lib/grammar/types'

import { kitVerbs } from '@/prakrit/lib/grammar/build'
import { AS_DHATU } from '@/prakrit/lib/grammar/verbs'
import { BASIC_TENSES, TENSES } from '@/prakrit/lib/grammar/types'
import { NUMBER_LABEL, PERSON_LABEL, TENSE_LABEL } from '@/prakrit/lib/grammar/labels'
import { latinKey, searchText } from '@/prakrit/components/grammar/labs/lab-search'

export type RootGroup = 'a' | 'long' | 'as'

export interface LabRoot {
    root: string
    hi: string
    en: string
    group: RootGroup
    rootClass: RootClass
    tables: Partial<Record<Tense, PersonTable>>
    search: string
}

export const ROOT_GROUPS: { value: RootGroup, hi: string, en: string }[] = [
    { value: 'a', hi: 'अकारान्त', en: 'ending in अ' },
    { value: 'long', hi: 'आ / ए / ओ', en: 'ending in आ, ए, ओ' },
    { value: 'as', hi: 'अस धातु', en: 'to be' }
]

let cache: LabRoot[] | null = null

/** All 103 kit roots (data/verbs.json order) and अस. */
export const labRoots = (): LabRoot[] => {
    if (!cache) {
        const kit: LabRoot[] = kitVerbs().map(v => {
            const tables: Partial<Record<Tense, PersonTable>> = {}

            for (const t of TENSES) {
                const grid = v.forms[t]

                if (grid) tables[t] = grid
            }

            return {
                root: v.root,
                hi: v.hi,
                en: v.en,
                group: v.class === 'long' ? 'long' : 'a',
                rootClass: v.class,
                tables,
                search: searchText(v.root, v.hi, v.en, latinKey(v.root))
            }
        })

        const as: LabRoot = {
            root: AS_DHATU.root,
            hi: AS_DHATU.hi,
            en: AS_DHATU.en,
            group: 'as',
            rootClass: 'a',
            tables: { vartaman: AS_DHATU.vartaman, bhoot: AS_DHATU.bhoot },
            search: searchText(AS_DHATU.root, AS_DHATU.hi, AS_DHATU.en, latinKey(AS_DHATU.root))
        }

        cache = [...kit, as]
    }

    return cache
}

export const findRoot = (root: string): LabRoot | undefined => labRoots().find(r => r.root === root)

export const isLabTense = (value: string): value is Tense => (TENSES as readonly string[]).includes(value)

/** The tenses taught first, in teaching order: वर्तमान, विधि-आज्ञा, भूत, भविष्यत्. */
export const basicTensesOf = (r: LabRoot): Tense[] => BASIC_TENSES.filter(t => r.tables[t])

/** कर्मवाच्य, प्रेरणार्थक and क्रियातिपत्ति tables this root has (Adults only). */
export const advancedTensesOf = (r: LabRoot): Tense[] => TENSES.filter(t => !BASIC_TENSES.includes(t) && r.tables[t])

const PERSON_NUMBERS: [Person, Num][] = [
    ['uttam', 'eka'],
    ['uttam', 'bahu'],
    ['madhyam', 'eka'],
    ['madhyam', 'bahu'],
    ['anya', 'eka'],
    ['anya', 'bahu']
]

/** The six cells of a 3 × 2 table in reading order. */
export const CELL_ORDER = PERSON_NUMBERS

// ---------------------------------------------------------------- present-tense meanings

const HI_SUBJECT: Record<Person, Record<Num, string>> = {
    uttam: { eka: 'मैं', bahu: 'हम' },
    madhyam: { eka: 'तुम', bahu: 'तुम सब' },
    anya: { eka: 'वह', bahu: 'वे' }
}

// सो (the subject of the अन्य एकवचन cell) is masculine, so the Hindi verb is masculine too.
const HI_PRESENT: Record<Person, Record<Num, string>> = {
    uttam: { eka: 'ता हूँ', bahu: 'ते हैं' },
    madhyam: { eka: 'ते हो', bahu: 'ते हो' },
    anya: { eka: 'ता है', bahu: 'ते हैं' }
}

const HI_BE: Record<Person, Record<Num, string>> = {
    uttam: { eka: 'हूँ', bahu: 'हैं' },
    madhyam: { eka: 'हो', bahu: 'हो' },
    anya: { eka: 'है', bahu: 'हैं' }
}

const EN_SUBJECT: Record<Person, Record<Num, string>> = {
    uttam: { eka: 'I', bahu: 'we' },
    madhyam: { eka: 'you', bahu: 'you all' },
    anya: { eka: 'he', bahu: 'they' }
}

const EN_BE: Record<Person, Record<Num, string>> = {
    uttam: { eka: 'am', bahu: 'are' },
    madhyam: { eka: 'are', bahu: 'are' },
    anya: { eka: 'is', bahu: 'are' }
}

const DANGLING = new Set(['for', 'to', 'with', 'at', 'on', 'in', 'of', 'from'])

const thirdPerson = (verb: string): string => {
    if (verb === 'do') return 'does'
    if (verb === 'go') return 'goes'
    if (verb === 'have') return 'has'
    if (/(s|sh|ch|x|z|o)$/.test(verb)) return verb + 'es'
    if (/[^aeiou]y$/.test(verb)) return verb.slice(0, -1) + 'ies'

    return verb + 's'
}

const capitalise = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1)

/**
 * "मैं पढ़ता हूँ। · I read." for a present-tense cell, built from the root's dictionary meaning.
 * Returns null when the meaning cannot be turned into a correct sentence (then the lab shows the grammar instead).
 */
export const presentMeaning = (r: LabRoot, p: Person, n: Num): { hi: string, en: string } | null => {
    if (r.group === 'as') {
        return {
            hi: `${HI_SUBJECT[p][n]} ${HI_BE[p][n]}।`,
            en: `${capitalise(EN_SUBJECT[p][n])} ${EN_BE[p][n]}.`
        }
    }

    const hi = r.hi.trim()
    const en = r.en.trim()

    if (en === 'to be' || !hi.endsWith('ना') || /[,/;(]/.test(hi) || !en.startsWith('to ') || /[,/;(]/.test(en)) return null

    const words = en.slice(3).split(/\s+/).filter(Boolean)

    if (words.length === 0 || DANGLING.has(words[words.length - 1])) return null

    const [first, ...rest] = words
    const tail = rest.length > 0 ? ` ${rest.join(' ')}` : ''
    const verb = first === 'be' ? EN_BE[p][n] : p === 'anya' && n === 'eka' ? thirdPerson(first) : first

    return {
        hi: `${HI_SUBJECT[p][n]} ${hi.slice(0, -2)}${HI_PRESENT[p][n]}।`,
        en: `${capitalise(EN_SUBJECT[p][n])} ${verb}${tail}.`
    }
}

/** The grammatical description used instead of a translation (all tenses except the present). */
export const describeCell = (r: LabRoot, tense: Tense, p: Person, n: Num): { hi: string, en: string } => {
    const passive = tense.startsWith('karmani_')

    return {
        hi: `${PERSON_LABEL[p].hi} ${NUMBER_LABEL[n].hi} · ${TENSE_LABEL[tense].hi} · धातु ${r.root} (${r.hi})${passive ? ' — कर्मवाच्य में प्रथमा वाला शब्द वह है जिस पर क्रिया होती है' : ''}`,
        en: `${PERSON_LABEL[p].en}, ${NUMBER_LABEL[n].en} · ${TENSE_LABEL[tense].en} · root ${r.root} (${r.en})${passive ? '; in the passive the प्रथमा word is the one acted upon' : ''}`
    }
}

// ---------------------------------------------------------------- the rule of each tense, in words

export interface TenseNote {
    hi: string
    en: string
    rule: string
}

export const tenseNote = (tense: Tense, group: RootGroup): TenseNote => {
    if (group === 'as') {
        const is = AS_DHATU.vartaman.anya.eka[0]?.form ?? ''

        return tense === 'bhoot'
            ? { hi: AS_DHATU.note, en: `Both past forms serve every person and number. Negation joins ण with the present form ${is}.`, rule: 'पाठ १४' }
            : { hi: `अस (होना) के रूप अनियमित हैं — इन्हें वैसे ही याद करें। ${AS_DHATU.note}`, en: `The forms of अस (to be) are irregular: learn them as they are. Negation joins ण with ${is}.`, rule: 'पाठ १४' }
    }

    const long = group === 'long'

    if (tense === 'vartaman') {
        return long
            ? { hi: 'आ / ए / ओ से अन्त होने वाली धातु में प्रत्यय सीधे जुड़ते हैं: -मि, -मो · -सि, -इत्था · -इ, -न्ति।', en: 'Roots ending in आ, ए or ओ take the endings directly: -मि, -मो · -सि, -इत्था · -इ, -न्ति.', rule: 'नि.९–१०' }
            : { hi: 'उत्तम पुरुष में -मि / -मो (इनसे पहले अ → आ), मध्यम में -सि / -इत्था, अन्य में -इ / -न्ति।', en: 'Speaker: -मि / -मो (अ becomes आ before them); addressee: -सि / -इत्था; the other: -इ / -न्ति.', rule: 'नि.९–१०' }
    }

    if (tense === 'bhoot') {
        return long
            ? { hi: 'आ / ए / ओ-अन्त धातु + -ही, सभी पुरुष और वचन में एक ही रूप (विकल्प: -सी, -हीअ)।', en: 'Roots in आ, ए, ओ add -ही, one form for every person and number (variants -सी, -हीअ).', rule: 'नि.१२' }
            : { hi: 'भूत काल में सभी पुरुष और वचन में एक ही रूप: धातु + -ईअ (अन्तिम अ → ई)।', en: 'The past has one form for every person and number: the root + -ईअ.', rule: 'नि.११' }
    }

    if (tense === 'bhavishya') {
        return long
            ? { hi: 'धातु + -हिमि, -हामो · -हिसि, -हित्था · -हिइ, -हिंति (विकल्प: -स्सामि …)।', en: 'Root + -हिमि, -हामो · -हिसि, -हित्था · -हिइ, -हिंति (variants with -स्स-).', rule: 'नि.१३' }
            : { hi: 'पहले अ → इ, फिर प्रत्यय: -हिमि, -हामो · -हिसि, -हित्था · -हिइ, -हिंति (विकल्प: -स्सामि …)।', en: 'First अ becomes इ, then the endings -हिमि, -हामो · -हिसि, -हित्था · -हिइ, -हिंति (variants with -स्स-).', rule: 'नि.१३' }
    }

    if (tense === 'vidhi') {
        return { hi: 'विधि (चाहिए) और आज्ञा (आदेश): -मु, -मो · -हि, -ह · -उ, -न्तु। मना करने के लिए: मा + रूप।', en: 'Wish and command: -मु, -मो · -हि, -ह · -उ, -न्तु. To forbid: मा + the form.', rule: 'नि.१४' }
    }

    if (tense.startsWith('karmani_')) {
        return { hi: 'कर्मवाच्य: धातु + -ईअ या -इज्ज, फिर काल का प्रत्यय (अ → आ नहीं होता)। कर्ता तृतीया में, कर्म प्रथमा में; क्रिया कर्म के अनुसार बदलती है।', en: 'Passive: root + -ईअ or -इज्ज, then the tense ending (no lengthening). The doer goes into तृतीया, the object into प्रथमा, and the verb agrees with the object.', rule: 'नि.८१–९३' }
    }

    if (tense.startsWith('prerak_')) {
        return { hi: 'प्रेरणार्थक: धातु + -आव, फिर प्रत्यय (पाठ ८१ में -आवे वाले रूप भी)। जिससे काम करवाया जाए, वह तृतीया या द्वितीया में।', en: 'Causative: root + -आव, then the ending (पाठ ८१ also prints forms with -आवे). The person made to act goes into तृतीया or द्वितीया.', rule: 'नि.९४–१००' }
    }

    return long
        ? { hi: 'क्रियातिपत्ति: सभी पुरुष-वचन में एक-से रूप — -ज्ज, -ज्जा, -न्तो, -माणो। "जइ … ता …" (यदि … तो …) वाले वाक्यों में।', en: 'Conditional: the same forms for every person and number: -ज्ज, -ज्जा, -न्तो, -माणो. Used in "जइ … ता …" (if … then …) sentences.', rule: 'नि.१०१–१०२' }
        : { hi: 'क्रियातिपत्ति: सभी पुरुष-वचन में एक-से रूप — -ज्ज, -ज्जा (अ → ए), -न्तो, -माणो। "जइ … ता …" (यदि … तो …) वाले वाक्यों में।', en: 'Conditional: the same forms for every person and number: -ज्ज, -ज्जा (अ becomes ए), -न्तो, -माणो. Used in "जइ … ता …" (if … then …) sentences.', rule: 'नि.१०१–१०२' }
}
